import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './RSVP.css';

const RSVP = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    attendingHouston: 'yes',
    attendingCancun: 'yes',
    houstonGuests: 0,
    cancunGuests: 0,
    message: '',
    householdResponses: [], // Will store responses for each household member
    additionalGuests: [] // Will store names and responses for additional guests
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [guestInfo, setGuestInfo] = useState(null);
  
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Add new state for existing RSVP
  const [existingRSVP, setExistingRSVP] = useState(null);
  
  // Load the user's name from localStorage and fetch guest info when component mounts
  useEffect(() => {
    const userName = localStorage.getItem('userName');
    if (userName) {
      setFormData(prevData => ({
        ...prevData,
        name: userName
      }));
      
      // First check if user has an existing RSVP
      checkExistingRSVP(userName);
    } else {
      setLoading(false);
    }
  }, []);
  
  // New function to check for existing RSVP
  const checkExistingRSVP = async (name) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/rsvp?name=${encodeURIComponent(name)}`);
      
      if (!response.ok) {
        // If no RSVP exists, proceed to fetch guest info
        if (response.status === 404) {
          fetchGuestInfo(name);
          return;
        }
        throw new Error('Failed to check existing RSVP');
      }
      
      const data = await response.json();
      setExistingRSVP(data);
      setLoading(false);
      
    } catch (err) {
      console.error('Error checking existing RSVP:', err);
      // If there's an error checking RSVP, still try to fetch guest info
      fetchGuestInfo(name);
    }
  };
  
  const fetchGuestInfo = async (name) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/guest-info?name=${encodeURIComponent(name)}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch guest information');
      }
      
      const data = await response.json();
      setGuestInfo(data);
      
      // Initialize household responses with default values
      const householdResponses = data.householdMembers ? 
        data.householdMembers
          .filter(member => member.name !== data.name) // Exclude the current user
          .map(member => ({
            name: member.name,
            attendingHouston: 'yes',
            attendingCancun: 'yes',
            event: member.event
          })) : [];
      
      // Update form data with guest info
      setFormData(prevData => ({
        ...prevData,
        name: data.name || prevData.name,
        // Initialize guest counts based on API data
        houstonGuests: 0, // Start with 0 additional guests
        cancunGuests: 0, // Start with 0 additional guests
        householdResponses,
        additionalGuests: [] // Initialize empty additional guests array
      }));
      
    } catch (err) {
      console.error('Error fetching guest info:', err);
      setError('Unable to load your guest information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to get the names of household heads
  const getHouseholdHeads = () => {
    if (!guestInfo || !guestInfo.householdMembers) return [];
    
    return guestInfo.householdMembers
      .filter(member => member.headOfHousehold)
      .map(member => member.name);
  };
  
  // Format the list of household heads for display
  const formatHouseholdHeadsList = (heads) => {
    if (heads.length === 0) return '';
    if (heads.length === 1) return heads[0];
    if (heads.length === 2) return `${heads[0]} or ${heads[1]}`;
    
    const lastHead = heads[heads.length - 1];
    const otherHeads = heads.slice(0, heads.length - 1).join(', ');
    return `${otherHeads}, or ${lastHead}`;
  };
  
  // Add a new function to format phone numbers
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Check if it's likely a US number (10 or 11 digits)
    if (phoneNumber.length === 10) {
      // Format as (xxx) - xxx - xxxx
      return `(${phoneNumber.substring(0, 3)}) - ${phoneNumber.substring(3, 6)} - ${phoneNumber.substring(6, 10)}`;
    } else if (phoneNumber.length === 11 && phoneNumber.charAt(0) === '1') {
      // Format as 1 (xxx) - xxx - xxxx for US numbers with country code
      return `1 (${phoneNumber.substring(1, 4)}) - ${phoneNumber.substring(4, 7)} - ${phoneNumber.substring(7, 11)}`;
    } else {
      // For international numbers, use a more flexible format
      // If the input already has formatting (contains non-digits), preserve it
      if (value.match(/\D/g)) {
        return value;
      }
      
      // Otherwise add some basic formatting for international numbers
      if (phoneNumber.length > 10) {
        // Try to format international numbers with country code
        const countryCode = phoneNumber.substring(0, phoneNumber.length - 10);
        const areaCode = phoneNumber.substring(phoneNumber.length - 10, phoneNumber.length - 7);
        const middle = phoneNumber.substring(phoneNumber.length - 7, phoneNumber.length - 4);
        const last = phoneNumber.substring(phoneNumber.length - 4);
        return `+${countryCode} (${areaCode})-${middle}-${last}`;
      }
      
      // Return the raw input if we can't determine a format
      return value;
    }
  };
  
  // Modify the handleChange function to format phone numbers
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If the field is 'phone', format the value
    if (name === 'phone') {
      setFormData({
        ...formData,
        [name]: formatPhoneNumber(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  // Handle changes to household member responses
  const handleHouseholdMemberChange = (index, field, value) => {
    const updatedResponses = [...formData.householdResponses];
    updatedResponses[index] = {
      ...updatedResponses[index],
      [field]: value
    };
    
    setFormData({
      ...formData,
      householdResponses: updatedResponses
    });
  };
  
  // Handle changes to additional guest responses
  const handleAdditionalGuestChange = (index, field, value) => {
    const updatedGuests = [...formData.additionalGuests];
    updatedGuests[index] = {
      ...updatedGuests[index],
      [field]: value
    };
    
    setFormData({
      ...formData,
      additionalGuests: updatedGuests
    });
  };
  
  // Update the number of additional guests and initialize their data
  const handleGuestCountChange = (e) => {
    const { name, value } = e.target;
    const count = parseInt(value, 10) || 0;
    
    setFormData(prevData => {
      // Create or update the additional guests array based on the new count
      let updatedAdditionalGuests = [...prevData.additionalGuests];
      
      if (count > updatedAdditionalGuests.length) {
        // Add new guests with just names, no attendance info yet
        for (let i = updatedAdditionalGuests.length; i < count; i++) {
          updatedAdditionalGuests.push({
            name: '',
            attendingHouston: 'yes',
            attendingCancun: 'yes'
          });
        }
      } else if (count < updatedAdditionalGuests.length) {
        // Remove excess guests
        updatedAdditionalGuests = updatedAdditionalGuests.slice(0, count);
      }
      
      return {
        ...prevData,
        [name]: count,
        additionalGuests: updatedAdditionalGuests
      };
    });
  };
  
  // Add a helper function to validate phone numbers
  const isValidPhoneNumber = (phone) => {
    // Basic validation - ensure there are at least 10 digits in the string
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 10;
  };
  
  // Modify the handleNextStep function to validate phone number
  const handleNextStep = (e) => {
    e.preventDefault();
    
    // Validate phone number before proceeding
    if (!isValidPhoneNumber(formData.phone)) {
      alert('Please enter a valid phone number with at least 10 digits');
      return;
    }
    
    // Check if we need to show the additional guests page
    const hasAdditionalGuests = 
      (guestInfo?.unknownHoustonGuests > 0 || guestInfo?.unknownCancunGuests > 0);
    
    if (step === 1 && hasAdditionalGuests) {
      setStep(2); // Go to additional guests page
    } else if (step === 1 || step === 2) {
      setStep(3); // Go to final RSVP page
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    
    try {
      // Prepare the primary guest RSVP data
      const primaryGuestRsvp = {
        name: formData.name,
        event: guestInfo.event,
        householdID: guestInfo.householdID || '',
        headOfHousehold: guestInfo.headOfHousehold || false,
        email: formData.email,
        phone: formData.phone,
        houstonRSVP: formData.attendingHouston === 'yes',
        cancunRSVP: formData.attendingCancun === 'yes',
        submittedForm: true
      };
      
      // Prepare household members RSVP data
      const householdRsvps = formData.householdResponses.map(member => ({
        name: member.name,
        event: member.event,
        householdID: guestInfo.householdID || '',
        headOfHousehold: false,
        email: '',
        houstonRSVP: member.attendingHouston === 'yes',
        cancunRSVP: member.attendingCancun === 'yes',
        submittedForm: false
      }));
      
      // Prepare additional guests RSVP data
      const additionalGuestsRsvps = formData.additionalGuests.map(guest => ({
        name: guest.name,
        event: 'Both', // Default to Both since they're additional guests
        householdID: guestInfo.householdID || '',
        headOfHousehold: false,
        email: '',
        houstonRSVP: guest.attendingHouston === 'yes',
        cancunRSVP: guest.attendingCancun === 'yes',
        submittedForm: false
      }));
      
      // Combine all RSVPs
      const allRsvps = [primaryGuestRsvp, ...householdRsvps, ...additionalGuestsRsvps];
      
      // Prepare the final payload
      const payload = {
        rsvps: allRsvps,
        noteToTheCouple: formData.message
      };
      
      // Send the data to the API
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/rsvp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit RSVP');
      }
      
      // If successful, set submitted to true
      setSubmitted(true);
      
    } catch (err) {
      console.error('Error submitting RSVP:', err);
      setSubmitError(err.message || 'Failed to submit your RSVP. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Helper function to check if guest is invited to a specific event
  const isInvitedTo = (event) => {
    if (!guestInfo) return false;
    return guestInfo.event === event || guestInfo.event === 'Both';
  };
  
  // Helper function to check if household member is invited to a specific event
  const isMemberInvitedTo = (memberEvent, event) => {
    return memberEvent === event || memberEvent === 'Both';
  };
  
  // Helper function to generate options for additional guests dropdown
  const generateGuestOptions = (maxGuests) => {
    const options = [];
    for (let i = 0; i <= maxGuests; i++) {
      options.push(
        <option key={i} value={i}>{i}</option>
      );
    }
    return options;
  };
  
  // Check if user is not head of household but has household members
  const isNonHeadWithHousehold = () => {
    return guestInfo && 
           guestInfo.householdMembers && 
           guestInfo.householdMembers.length > 0 && 
           !guestInfo.headOfHousehold;
  };
  
  // Check if we should show the additional guests page
  const shouldShowAdditionalGuestsPage = () => {
    return guestInfo && 
           (guestInfo.unknownHoustonGuests > 0 || guestInfo.unknownCancunGuests > 0);
  };
  
  // Add a new component to display existing RSVP
  const ExistingRSVPDisplay = () => {
    if (!existingRSVP || !existingRSVP.rsvp) return null;
    
    const rsvp = existingRSVP.rsvp;
    const householdMembers = existingRSVP.householdMembers || [];
    
    return (
      <div className="existing-rsvp">
        <h2>Your RSVP Submission</h2>
        <p className="rsvp-note">You have already submitted your RSVP. If you need to make changes, please contact us directly.</p>
        
        <div className="rsvp-details">
          <h3>Your Information</h3>
          <div className="rsvp-info-item">
            <span className="label">Name:</span>
            <span className="value">{rsvp.name}</span>
          </div>
          <div className="rsvp-info-item">
            <span className="label">Email:</span>
            <span className="value">{rsvp.email}</span>
          </div>
          <div className="rsvp-info-item">
            <span className="label">Phone:</span>
            <span className="value">{rsvp.phone}</span>
          </div>
          
          {rsvp.invitedTo === 'Houston' || rsvp.invitedTo === 'Both' ? (
            <div className="rsvp-info-item">
              <span className="label">Houston Reception:</span>
              <span className="value">{rsvp.houstonRSVP}</span>
            </div>
          ) : null}
          
          {rsvp.invitedTo === 'Cancun' || rsvp.invitedTo === 'Both' ? (
            <div className="rsvp-info-item">
              <span className="label">Cancun Wedding:</span>
              <span className="value">{rsvp.cancunRSVP}</span>
            </div>
          ) : null}
          
          {rsvp.noteToTheCouple && (
            <div className="rsvp-info-item">
              <span className="label">Your Message:</span>
              <span className="value note">{rsvp.noteToTheCouple}</span>
            </div>
          )}
        </div>
        
        {householdMembers.length > 0 && (
          <div className="household-rsvp-details">
            <h3>Guests</h3>
            {householdMembers.map((member, index) => (
              <div key={index} className="household-member">
                <h4>{member.name}</h4>
                
                {member.invitedTo === 'Houston' || member.invitedTo === 'Both' ? (
                  <div className="rsvp-info-item">
                    <span className="label">Houston Reception:</span>
                    <span className="value">{member.houstonRSVP}</span>
                  </div>
                ) : null}
                
                {member.invitedTo === 'Cancun' || member.invitedTo === 'Both' ? (
                  <div className="rsvp-info-item">
                    <span className="label">Cancun Wedding:</span>
                    <span className="value">{member.cancunRSVP}</span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
        
        <div className="contact-info">
          <p>Need to make changes to your RSVP?</p>
          <p>Please contact us at <a href="mailto:contact@example.com">contact@example.com</a></p>
        </div>
      </div>
    );
  };
  
  if (existingRSVP) {
    return (
      <div className="page-container rsvp-container">
        <h1>RSVP</h1>
        <ExistingRSVPDisplay />
      </div>
    );
  }
  
  if (submitted) {
    return (
      <div className="page-container rsvp-container">
        <div className="rsvp-confirmation">
          <h2>Thank You!</h2>
          <p>Your RSVP has been submitted successfully. We look forward to celebrating with you!</p>
          <button onClick={() => navigate('/')} className="back-button">
            Back to Home
          </button>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="page-container rsvp-container">
        <div className="loading-container">
          <h2>Loading your information...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="page-container rsvp-container">
        <div className="error-container">
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  // Show message for non-head of household members
  if (isNonHeadWithHousehold()) {
    const householdHeads = getHouseholdHeads();
    const headsFormatted = formatHouseholdHeadsList(householdHeads);
    
    return (
      <div className="page-container rsvp-container">
        <h1>RSVP</h1>
        <div className="non-head-message">
          <h2>RSVP Information</h2>
          <p>
            We noticed that you are part of a group, but you are not designated as the primary guest.
          </p>
          <p>
            For your group, {headsFormatted} {householdHeads.length > 1 ? 'are' : 'is'} designated to submit the RSVP for all guests.
          </p>
          <p>
            Please ask {householdHeads.length > 1 ? 'one of them' : 'them'} to complete the RSVP for your group.
          </p>
          <p className="contact-note">
            If you believe this is an error or have any questions, please contact us.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="page-container rsvp-container">
      <h1>RSVP</h1>
      <p className="rsvp-intro">
        Please let us know if you'll be joining us on our special day by August 1, 2025.
      </p>
      
      {/* Step 1: Basic Information */}
      {step === 1 && (
        <form className="rsvp-form" onSubmit={handleNextStep}>
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              readOnly={formData.name !== ''}
              className={formData.name !== '' ? 'prefilled' : ''}
            />
            {formData.name !== '' && (
              <p className="prefilled-note">Name pre-filled from your login information</p>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email address"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="Enter your phone number"
              title="Please enter a valid phone number"
            />
          </div>
          
          <button type="submit" className="next-button">
            Next
          </button>
        </form>
      )}
      
      {/* Step 2: Additional Guests Information */}
      {step === 2 && shouldShowAdditionalGuestsPage() && (
        <form className="rsvp-form" onSubmit={handleNextStep}>
          <div className="form-summary">
            <p><strong>Name:</strong> {formData.name}</p>
            <p><strong>Email:</strong> {formData.email}</p>
            <p><strong>Phone:</strong> {formData.phone}</p>
          </div>
          
          <h3>Additional Guests</h3>
          
          {/* Houston Additional Guests */}
          {isInvitedTo('Houston') || isInvitedTo('Both') ? (
            guestInfo.unknownHoustonGuests > 0 ? (
              <div className="additional-guests-section">
                <h4>Houston Reception Guests</h4>
                <div className="form-group">
                  <label htmlFor="houstonGuests">How many additional guests will you bring to the Houston reception?</label>
                  <select
                    id="houstonGuests"
                    name="houstonGuests"
                    value={formData.houstonGuests}
                    onChange={handleGuestCountChange}
                  >
                    {generateGuestOptions(guestInfo.unknownHoustonGuests)}
                  </select>
                  <p className="guest-note">You may bring up to {guestInfo.unknownHoustonGuests} additional guests</p>
                </div>
              </div>
            ) : null
          ) : null}
          
          {/* Cancun Additional Guests */}
          {isInvitedTo('Cancun') || isInvitedTo('Both') ? (
            guestInfo.unknownCancunGuests > 0 ? (
              <div className="additional-guests-section">
                <h4>Cancun Wedding Guests</h4>
                <div className="form-group">
                  <label htmlFor="cancunGuests">How many additional guests will you bring to the Cancun wedding?</label>
                  <select
                    id="cancunGuests"
                    name="cancunGuests"
                    value={formData.cancunGuests}
                    onChange={handleGuestCountChange}
                  >
                    {generateGuestOptions(guestInfo.unknownCancunGuests)}
                  </select>
                  <p className="guest-note">You may bring up to {guestInfo.unknownCancunGuests} additional guests</p>
                </div>
              </div>
            ) : null
          ) : null}
          
          {/* Guest Names Input Fields - SIMPLIFIED to only collect names */}
          {formData.additionalGuests.length > 0 && (
            <div className="guest-names-section">
              <h4>Guest Information</h4>
              <p>Please provide the names of your additional guests:</p>
              
              {formData.additionalGuests.map((guest, index) => (
                <div key={index} className="guest-name-input">
                  <div className="form-group">
                    <label htmlFor={`guest-${index}-name`}>Guest {index + 1} Name</label>
                    <input
                      type="text"
                      id={`guest-${index}-name`}
                      value={guest.name}
                      onChange={(e) => handleAdditionalGuestChange(index, 'name', e.target.value)}
                      required
                      placeholder="Enter guest's full name"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="button-group">
            <button type="button" className="back-button" onClick={() => setStep(1)}>
              Back
            </button>
            <button type="submit" className="next-button">
              Next
            </button>
          </div>
        </form>
      )}
      
      {/* Step 3: Final RSVP Form */}
      {step === 3 && (
        <form className="rsvp-form" onSubmit={handleSubmit}>
          <div className="form-summary">
            <p><strong>Name:</strong> {formData.name}</p>
            <p><strong>Email:</strong> {formData.email}</p>
            <p><strong>Phone:</strong> {formData.phone}</p>
          </div>
          
          {guestInfo && guestInfo.householdMembers && guestInfo.householdMembers.length > 0 && (
            <div className="household-info">
              <h3>Your Group Information</h3>
              <p>You are {guestInfo.headOfHousehold ? 'the primary guest' : 'a member'} of your guest group.</p>
            </div>
          )}
          
          {/* Your RSVP */}
          <div className="your-rsvp">
            <h3>Your Response</h3>
            
            {/* Houston Event RSVP */}
            {isInvitedTo('Houston') || isInvitedTo('Both') ? (
              <div className="event-rsvp houston-event">
                <h4>Houston Reception</h4>
                <p className="event-details">Saturday, November 29th, 2025</p>
                
                <div className="form-group">
                  <label>Will you be attending our reception in Houston?</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        name="attendingHouston"
                        value="yes"
                        checked={formData.attendingHouston === 'yes'}
                        onChange={handleChange}
                      />
                      Joyfully Accept
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="attendingHouston"
                        value="no"
                        checked={formData.attendingHouston === 'no'}
                        onChange={handleChange}
                      />
                      Regretfully Decline
                    </label>
                  </div>
                </div>
              </div>
            ) : null}
            
            {/* Cancun Event RSVP */}
            {isInvitedTo('Cancun') || isInvitedTo('Both') ? (
              <div className="event-rsvp cancun-event">
                <h4>Cancun Wedding Celebration</h4>
                <p className="event-details">Friday, December 5th to Sunday, December 7th, 2025</p>
                
                <div className="form-group">
                  <label>Will you be attending our wedding celebration in Cancun, Mexico?</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        name="attendingCancun"
                        value="yes"
                        checked={formData.attendingCancun === 'yes'}
                        onChange={handleChange}
                      />
                      Joyfully Accept
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="attendingCancun"
                        value="no"
                        checked={formData.attendingCancun === 'no'}
                        onChange={handleChange}
                      />
                      Regretfully Decline
                    </label>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          
          {/* Household Members RSVP */}
          {formData.householdResponses.length > 0 && (
            <div className="household-members-rsvp">
              <h3>Guests</h3>
              <p>Please indicate which events each guest in your group will attend:</p>
              
              {formData.householdResponses.map((member, index) => (
                <div key={index} className="member-rsvp">
                  <h4>{member.name}</h4>
                  
                  {/* Houston Event for household member */}
                  {isMemberInvitedTo(member.event, 'Houston') || isMemberInvitedTo(member.event, 'Both') ? (
                    <div className="form-group">
                      <label>Will {member.name} attend the Houston Reception?</label>
                      <div className="radio-group">
                        <label>
                          <input
                            type="radio"
                            name={`member-${index}-houston`}
                            value="yes"
                            checked={member.attendingHouston === 'yes'}
                            onChange={() => handleHouseholdMemberChange(index, 'attendingHouston', 'yes')}
                          />
                          Yes
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`member-${index}-houston`}
                            value="no"
                            checked={member.attendingHouston === 'no'}
                            onChange={() => handleHouseholdMemberChange(index, 'attendingHouston', 'no')}
                          />
                          No
                        </label>
                      </div>
                    </div>
                  ) : null}
                  
                  {/* Cancun Event for household member */}
                  {isMemberInvitedTo(member.event, 'Cancun') || isMemberInvitedTo(member.event, 'Both') ? (
                    <div className="form-group">
                      <label>Will {member.name} attend the Cancun Wedding?</label>
                      <div className="radio-group">
                        <label>
                          <input
                            type="radio"
                            name={`member-${index}-cancun`}
                            value="yes"
                            checked={member.attendingCancun === 'yes'}
                            onChange={() => handleHouseholdMemberChange(index, 'attendingCancun', 'yes')}
                          />
                          Yes
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`member-${index}-cancun`}
                            value="no"
                            checked={member.attendingCancun === 'no'}
                            onChange={() => handleHouseholdMemberChange(index, 'attendingCancun', 'no')}
                          />
                          No
                        </label>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
          
          {/* Additional Guests RSVP Summary */}
          {formData.additionalGuests.length > 0 && (
            <div className="additional-guests-rsvp">
              <h3>Additional Guests</h3>
              <p>The following additional guests will be attending with you:</p>
              
              {formData.additionalGuests.map((guest, index) => (
                <div key={index} className="guest-rsvp-summary">
                  <h4>{guest.name || `Guest ${index + 1}`}</h4>
                  
                  {isInvitedTo('Houston') || isInvitedTo('Both') ? (
                    <div className="form-group">
                      <label>Will {guest.name || `Guest ${index + 1}`} attend the Houston Reception?</label>
                      <div className="radio-group">
                        <label>
                          <input
                            type="radio"
                            name={`guest-${index}-houston`}
                            value="yes"
                            checked={guest.attendingHouston === 'yes'}
                            onChange={() => handleAdditionalGuestChange(index, 'attendingHouston', 'yes')}
                          />
                          Yes
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`guest-${index}-houston`}
                            value="no"
                            checked={guest.attendingHouston === 'no'}
                            onChange={() => handleAdditionalGuestChange(index, 'attendingHouston', 'no')}
                          />
                          No
                        </label>
                      </div>
                    </div>
                  ) : null}
                  
                  {isInvitedTo('Cancun') || isInvitedTo('Both') ? (
                    <div className="form-group">
                      <label>Will {guest.name || `Guest ${index + 1}`} attend the Cancun Wedding?</label>
                      <div className="radio-group">
                        <label>
                          <input
                            type="radio"
                            name={`guest-${index}-cancun`}
                            value="yes"
                            checked={guest.attendingCancun === 'yes'}
                            onChange={() => handleAdditionalGuestChange(index, 'attendingCancun', 'yes')}
                          />
                          Yes
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`guest-${index}-cancun`}
                            value="no"
                            checked={guest.attendingCancun === 'no'}
                            onChange={() => handleAdditionalGuestChange(index, 'attendingCancun', 'no')}
                          />
                          No
                        </label>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="message">Message to the Couple (Optional)</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Share your well wishes or thoughts with us."
            ></textarea>
          </div>
          
          {submitError && (
            <div className="error-message">
              <p>{submitError}</p>
            </div>
          )}
          
          <div className="button-group">
            <button 
              type="button" 
              className="back-button" 
              onClick={() => setStep(shouldShowAdditionalGuestsPage() ? 2 : 1)}
              disabled={submitting}
            >
              Back
            </button>
            <button 
              type="submit" 
              className="submit-button"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit RSVP'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default RSVP; 